import { useState } from 'react';
import {
    Paper,
    Group,
    ThemeIcon,
    Title,
    Text,
    Button,
    Modal,
    Table,
    Badge,
    Divider,
    Stack,
} from '@mantine/core';
import { IconEye, IconUsers } from '@tabler/icons-react';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface KycTableColumn<T> {
    label: string;
    key: keyof T;
    render?: (value: T[keyof T], row: T) => React.ReactNode;
}

export interface KycTableSection<T extends Record<string, unknown>> {
    title: string;
    data: T[] | null | undefined;
    columns: KycTableColumn<T>[];
    icon?: React.ReactNode;
}

export interface KycListTableViewerProps {
    cardTitle: string;
    children?: React.ReactNode;
    cardIcon?: React.ReactNode;
    accentColor?: string;
    sections: KycTableSection<Record<string, unknown>>[];
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export function KycListTableViewer({
    cardTitle,
    cardIcon = <IconUsers size={16} />,
    accentColor = 'red',
    sections,
    children,
}: KycListTableViewerProps) {
    const [openedIndex, setOpenedIndex] = useState<number | null>(null);

    const visibleSections = sections.filter((s) => s.data && s.data.length > 0);
    if (visibleSections.length === 0) return null;

    const openedSection = openedIndex !== null ? visibleSections[openedIndex] : null;

    return (
        <>
            {/* ── Card ── */}
            <Paper
                shadow="xs"
                p="md"
                style={{
                    borderLeft: `3px solid var(--mantine-color-${accentColor}-6)`,
                    borderRadius: 8,
                }}
            >
                {/* Card Header */}
                <Group gap="sm" wrap="nowrap" mb="sm">
                    <ThemeIcon color={accentColor} variant="light" size="md" radius="md">
                        {cardIcon}
                    </ThemeIcon>
                    <Title order={5} style={{ flex: 1 }}>
                        {cardTitle}
                    </Title>
                </Group>

                <Divider mb="sm" />

                {/* Section Rows */}
                <Stack gap="xs">
                    {visibleSections.map((section, index) => (
                        <Group key={index} justify="space-between" align="center" wrap="nowrap">
                            <Group gap="xs" wrap="nowrap">
                                <ThemeIcon color={accentColor} variant="subtle" size="sm" radius="md">
                                    {section.icon ?? <IconUsers size={13} />}
                                </ThemeIcon>
                                <Text size="sm" fw={500}>
                                    {section.title}
                                </Text>
                                <Badge color={accentColor} variant="light" size="sm" radius="sm">
                                    {section.data!.length}
                                </Badge>
                            </Group>

                            <Button
                                size="xs"
                                variant="light"
                                color={accentColor}
                                leftSection={<IconEye size={13} />}
                                radius="md"
                                onClick={() => setOpenedIndex(index)}
                            >
                                View
                            </Button>
                        </Group>
                    ))}
                </Stack>
                {children && <><Divider my="sm" /><Stack gap="xs">{children}</Stack></>}
            </Paper>

            {/* ── Modal ── */}
            <Modal
                opened={openedIndex !== null}
                onClose={() => setOpenedIndex(null)}
                title={
                    openedSection && (
                        <Group gap="xs" align="center">
                            <ThemeIcon color={accentColor} variant="light" size="sm" radius="md">
                                {openedSection.icon ?? <IconUsers size={14} />}
                            </ThemeIcon>
                            <Title order={5}>{openedSection.title}</Title>
                            <Badge color={accentColor} variant="light" size="sm" radius="sm">
                                {openedSection.data!.length}
                            </Badge>
                        </Group>
                    )
                }
                size="xl"
                centered
                radius="md"
            >
                {openedSection && (
                    <Table.ScrollContainer minWidth={650}>
                    <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                            <Table.Tr>
                                {openedSection.columns.map((col) => (
                                    <Table.Th key={String(col.key)}>{col.label}</Table.Th>
                                ))}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {openedSection.data!.map((row, rowIndex) => (
                                <Table.Tr key={rowIndex}>
                                    {openedSection.columns.map((col) => (
                                        <Table.Td key={String(col.key)}>
                                            {col.render
                                                ? col.render(row[col.key], row)
                                                : (row[col.key] as string) ?? ''}
                                        </Table.Td>
                                    ))}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    </Table.ScrollContainer>
                )}
            </Modal>
        </>
    );
}